import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  StyleSheet,
  StatusBar,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useAppDispatch, useAppSelector } from '@/store';
import { fetchLinkedAccounts, deleteLinkedAccount } from '@/store/slices/residents/homeSlice';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { LinkedAccount, Invitation } from '@/types/residents/home';
import { declineInvitation } from '@/store/slices/residents/invitationsSlice';
import { ConfirmationModal } from '@/components/residents/marketplace/ConfirmationModal';
import { getDisplayName } from '@/utils/residents/utils';
import { useResponsive } from '@/hooks/common/useResponsive';
import { DesktopLinkedAccountsView } from '@/screens/residents/linkedAccounts/desktop/DesktopLinkedAccountsView';
import { DesktopInviteFormModal } from '@/components/residents/invitations/DesktopInviteFormModal';
import { useToast } from '@/contexts/admin/ToastContext';
import { GradientBackground } from '@/components/residents/redesign';
import { BaseResidentMobileScreen } from '@/components/residents/mobile/BaseResidentMobileScreen';
import { BackLink } from '@/components/residents/mobile/BackLink';

type LinkedAccountsNavigationProp = StackNavigationProp<RootStackParamList>;

export const LinkedAccountsScreen: React.FC = () => {
  const navigation = useNavigation<LinkedAccountsNavigationProp>();
  const dispatch = useAppDispatch();
  const { linkedAccounts, isLoading } = useAppSelector((state) => state.home);
  const { layoutMode } = useResponsive();
  const { showSuccess, showError } = useToast();

  // Remove Connection Modal
  const [isRemoveModalVisible, setIsRemoveModalVisible] = useState(false);
  const [accountToRemove, setAccountToRemove] = useState<LinkedAccount | null>(null);

  // Decline Invitation Modal
  const [isDeclineModalVisible, setIsDeclineModalVisible] = useState(false);
  const [invitationToDecline, setInvitationToDecline] = useState<Invitation | null>(null);

  // Cancel Invitation Modal
  const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);
  const [invitationToCancel, setInvitationToCancel] = useState<Invitation | null>(null);

  // Send Invitation Modal (desktop)
  const [isInviteModalVisible, setIsInviteModalVisible] = useState(false);

  useEffect(() => {
    dispatch(fetchLinkedAccounts());
  }, [dispatch]);

  const handleBack = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  const handleRefresh = useCallback(() => {
    dispatch(fetchLinkedAccounts());
  }, [dispatch]);

  // Remove Connection
  const handleRemovePress = useCallback((account: LinkedAccount) => {
    setAccountToRemove(account);
    setIsRemoveModalVisible(true);
  }, []);

  const handleRemoveCancel = useCallback(() => {
    setIsRemoveModalVisible(false);
    setAccountToRemove(null);
  }, []);

  const handleRemoveProceed = useCallback(async () => {
    if (!accountToRemove) return;
    setIsRemoveModalVisible(false);
    try {
      await dispatch(deleteLinkedAccount(accountToRemove.id)).unwrap();
      const displayName = getDisplayName(accountToRemove.user.firstName, accountToRemove.user.lastName) || accountToRemove.user.email;
      showSuccess(`You have successfully disconnected from ${displayName}`);
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Failed to remove linked account');
    } finally {
      setAccountToRemove(null);
    }
  }, [accountToRemove, dispatch, showSuccess, showError]);

  const getRemoveConfirmMessage = (): string => {
    if (!accountToRemove) return '';
    const displayName = getDisplayName(accountToRemove.user.firstName, accountToRemove.user.lastName) || accountToRemove.user.email;
    return `Are you sure you want to remove connection to ${displayName}?`;
  };

  // Decline Invitation
  const handleDeclineInvitation = useCallback((invitation: Invitation) => {
    setInvitationToDecline(invitation);
    setIsDeclineModalVisible(true);
  }, []);

  const handleDeclineCancel = useCallback(() => {
    setIsDeclineModalVisible(false);
    setInvitationToDecline(null);
  }, []);

  const handleDeclineProceed = useCallback(async () => {
    if (!invitationToDecline) return;
    setIsDeclineModalVisible(false);
    try {
      await dispatch(declineInvitation({ invitationId: invitationToDecline.id, sender: invitationToDecline.sender })).unwrap();
      const displayName = getDisplayName(invitationToDecline.user.firstName, invitationToDecline.user.lastName) || invitationToDecline.user.email;
      showSuccess(`You have successfully declined an invitation from ${displayName}`);
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Failed to decline invitation');
    } finally {
      setInvitationToDecline(null);
    }
  }, [invitationToDecline, dispatch, showSuccess, showError]);

  const getDeclineConfirmMessage = (): string => {
    if (!invitationToDecline) return '';
    const displayName = getDisplayName(invitationToDecline.user.firstName, invitationToDecline.user.lastName) || invitationToDecline.user.email;
    return `Are you sure you want to decline an invitation from ${displayName}?`;
  };

  // Cancel Invitation
  const handleCancelInvitation = useCallback((invitation: Invitation) => {
    setInvitationToCancel(invitation);
    setIsCancelModalVisible(true);
  }, []);

  const handleCancelConfirmCancel = useCallback(() => {
    setIsCancelModalVisible(false);
    setInvitationToCancel(null);
  }, []);

  const handleCancelProceed = useCallback(async () => {
    if (!invitationToCancel) return;
    setIsCancelModalVisible(false);
    try {
      await dispatch(declineInvitation({ invitationId: invitationToCancel.id, sender: invitationToCancel.sender })).unwrap();
      showSuccess(`Invitation cancelled for ${invitationToCancel.user.email}`);
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Failed to cancel invitation');
    } finally {
      setInvitationToCancel(null);
    }
  }, [invitationToCancel, dispatch, showSuccess, showError]);

  const getCancelConfirmMessage = (): string => {
    if (!invitationToCancel) return '';
    return `Are you sure you want to cancel an invitation for ${invitationToCancel.user.email}?`;
  };

  const handleSendInvite = useCallback(() => {
    if (layoutMode === 'desktop') {
      setIsInviteModalVisible(true);
    } else {
      navigation.navigate('NewInvitation');
    }
  }, [layoutMode, navigation]);

  const linkedAccountsView = (
    <DesktopLinkedAccountsView
      linkedAccounts={linkedAccounts}
      isLoading={isLoading}
      onRefresh={handleRefresh}
      onRemove={handleRemovePress}
      onSendInvite={handleSendInvite}
      onDeclineInvitation={handleDeclineInvitation}
      onCancelInvitation={handleCancelInvitation}
    />
  );

  const confirmationModals = (
    <>
      <ConfirmationModal
        visible={isRemoveModalVisible}
        message={getRemoveConfirmMessage()}
        onCancel={handleRemoveCancel}
        onProceed={handleRemoveProceed}
        cancelText="No, cancel"
        proceedText="Proceed"
      />

      <ConfirmationModal
        visible={isDeclineModalVisible}
        message={getDeclineConfirmMessage()}
        onCancel={handleDeclineCancel}
        onProceed={handleDeclineProceed}
        cancelText="No, cancel"
        proceedText="Proceed"
      />

      <ConfirmationModal
        visible={isCancelModalVisible}
        message={getCancelConfirmMessage()}
        onCancel={handleCancelConfirmCancel}
        onProceed={handleCancelProceed}
        cancelText="No, cancel"
        proceedText="Proceed"
      />
    </>
  );

  if (layoutMode === 'desktop') {
    return (
      <>
        {linkedAccountsView}
        {confirmationModals}
        <DesktopInviteFormModal
          visible={isInviteModalVisible}
          onClose={() => setIsInviteModalVisible(false)}
        />
      </>
    );
  }

  return (
    <GradientBackground>
      <StatusBar barStyle="light-content" />
      <BaseResidentMobileScreen
        screenName="LinkedAccounts"
        darkBackground
        headerVariant="gradient"
      >
        <View style={styles.backLinkWrapper}>
          <BackLink
            label="Back to settings"
            onPress={handleBack}
            color={PRIMITIVE_COLORS.resioBlue}
          />
        </View>
        <View style={styles.contentWrapper}>
          {linkedAccountsView}
        </View>
      </BaseResidentMobileScreen>
      {confirmationModals}
    </GradientBackground>
  );
};

const styles = StyleSheet.create({
  backLinkWrapper: {
    paddingHorizontal: REDESIGN_SPACING.horizontalPadding,
    paddingTop: 8,
  },
  contentWrapper: {
    flex: 1,
  },
});
